import { formatMelbourneDateTime } from "../../../utils/melbourneTime";

export const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;

/** A Gmail timestamp (milliseconds) in Melbourne time. */
export const formatMailDate = (ms) => (ms ? formatMelbourneDateTime(new Date(ms).toISOString()) : "");

export const personLabel = (p) => (p?.name ? `${p.name} <${p.email}>` : p?.email || "");

const addressList = (people) => people.map((p) => p.email).join(", ");

const prefixed = (prefix, subject) =>
  new RegExp(`^${prefix}:`, "i").test(subject || "") ? subject : `${prefix}: ${subject || ""}`.trim();

/** The readable text of an HTML email, for quoting in a reply or forward. */
export function htmlToText(html) {
  if (!html) return "";
  const withBreaks = html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|tr|li|h[1-6])>/gi, "\n");
  const doc = new DOMParser().parseFromString(withBreaks, "text/html");
  doc.querySelectorAll("style,script,head").forEach((n) => n.remove());
  return (doc.body?.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

const messageText = (m) => (m.body_text || htmlToText(m.body_html)).trim();

/**
 * The composer's starting point for a reply to `message`. `all` also copies
 * everyone else on it, leaving out the agent's own address.
 */
export function replyDraft(message, ownEmail, all = false) {
  const own = (ownEmail || "").toLowerCase();
  let to;
  if (message.sent) to = message.to; // replying to your own email: same people again
  else to = message.reply_to?.length ? message.reply_to : [message.from];
  to = to.filter((p) => p.email && p.email !== own);
  const taken = new Set([own, ...to.map((p) => p.email)]);
  const cc = [];
  if (all) {
    // With a Reply-To, the sender still gets a copy on reply all, as in Gmail.
    [message.from, ...message.to, ...message.cc].forEach((p) => {
      if (p.email && !taken.has(p.email)) {
        taken.add(p.email);
        cc.push(p);
      }
    });
  }
  const quoted = messageText(message).split("\n").map((line) => `> ${line}`).join("\n");
  return {
    to: addressList(to),
    cc: addressList(cc),
    subject: prefixed("Re", message.subject),
    body: `\n\nOn ${formatMailDate(message.date)}, ${personLabel(message.from)} wrote:\n${quoted}\n`,
    replyTo: message.id,
  };
}

/** The composer's starting point for forwarding `message`, with its attachments. */
export function forwardDraft(message) {
  const header = [
    "---------- Forwarded message ----------",
    `From: ${personLabel(message.from)}`,
    `Date: ${formatMailDate(message.date)}`,
    `Subject: ${message.subject || ""}`,
    `To: ${message.to.map(personLabel).join(", ")}`,
    ...(message.cc.length ? [`Cc: ${message.cc.map(personLabel).join(", ")}`] : []),
  ].join("\n");
  return {
    to: "",
    subject: prefixed("Fwd", message.subject),
    body: `\n\n${header}\n\n${messageText(message)}\n`,
    forwardOf: message.id,
    forwardAttachments: message.attachments || [],
  };
}
